import { Switch } from "antd";
import { useStoreCascadeMutation } from "api/TenantSlug/Cascade/hooks.ts";
import { ChannelItemFormData, channelItemSchema } from "components/Forms/PingtreeForm/formSchema.ts";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { CascadeAction, CascadeDistributionType, CascadeStatus } from "types/enums";

import StoreCascadeRequest = App.Http.Requests.Cascade.StoreCascadeRequest;

const NEW_CHANNEL: ChannelItemFormData = {
    title: "",
    visible_title: "",
    integration: "",
    distribution: "",
    action: "",
    bid_min_accepted: null,
    active: true,
};

interface AddCascadeFormProps {
    offerId: string;
    formId: string;
    sort: number;
    onSuccess?: () => void;
}

export const AddCascadeForm: FC<AddCascadeFormProps> = ({ offerId, formId, sort, onSuccess }) => {
    const { t: tF } = usePaldockTranslation("forms.add-cascade");

    const storeCascadeMutation = useStoreCascadeMutation({
        onError: () => toast.error(tF("messages.create_error")),
    });

    const handleSubmit = async (values: ChannelItemFormData) => {
        const request: StoreCascadeRequest = {
            offer_id: offerId,
            title: values.title,
            visible_title: values.visible_title,
            integration_id: values.integration,
            distribution: values.distribution as CascadeDistributionType,
            action: values.action as CascadeAction,
            bid_min_accepted: values.bid_min_accepted,
            filter: null,
            status: values.active ? CascadeStatus.Enabled : CascadeStatus.Disabled,
            sort,
        };

        try {
            await storeCascadeMutation.mutateAsync(request);
            toast.success(tF("messages.create_success"));
            onSuccess?.();
        } catch (error) {
            console.error("Error creating cascade:", error);
            throw error;
        }
    };

    return (
        <GeneralFormProvider<ChannelItemFormData>
            key="create"
            schema={channelItemSchema}
            defaultValues={NEW_CHANNEL}
            id={formId}
            onSubmit={handleSubmit}
        >
            <AddCascadeFormContent />
        </GeneralFormProvider>
    );
};

const AddCascadeFormContent: FC = () => {
    const { t: tF } = usePaldockTranslation("forms.add-cascade");
    const { t: tP } = usePaldockTranslation("pages.offer.edit.pingtree.columns");
    const { t: tG } = usePaldockTranslation("general");
    const { control } = useGeneralForm<ChannelItemFormData>();

    const distributionOptions = useMemo(
        () =>
            Object.values(CascadeDistributionType).map((value) => ({
                label: tG(`cascade_distribution_type.${value}`),
                value,
            })),
        [tG]
    );

    const actionOptions = useMemo(
        () =>
            Object.values(CascadeAction).map((value) => ({
                label: tG(`cascade_action.${value}`),
                value,
            })),
        [tG]
    );

    const list = PropertiesList.create(NEW_CHANNEL, [
        {
            term: tF("fields.title.label"),
            render: <InteractiveCell.Input formControl={{ control, name: "title" }} alwaysEditing />,
        },
        {
            term: tF("fields.visible_title.label"),
            render: <InteractiveCell.Input formControl={{ control, name: "visible_title" }} alwaysEditing />,
        },
        {
            term: tF("fields.integration.label"),
            render: <InteractiveCell.Integration formControl={{ control, name: "integration" }} alwaysEditing />,
        },
        {
            term: tF("fields.distribution.label"),
            render: (
                <InteractiveCell.Select
                    options={distributionOptions}
                    formControl={{ control, name: "distribution" }}
                    alwaysEditing
                />
            ),
        },
        {
            term: tF("fields.action.label"),
            render: (
                <InteractiveCell.Select
                    options={actionOptions}
                    formControl={{ control, name: "action" }}
                    alwaysEditing
                />
            ),
        },
        {
            term: tP("min_price"),
            render: (
                <InteractiveCell.NumberInput
                    formControl={{ control, name: "bid_min_accepted" }}
                    clearable
                    alwaysEditing
                />
            ),
        },
        {
            term: tF("fields.active.label"),
            render: (
                <FormItem control={control} name="active" noStyle>
                    <Switch />
                </FormItem>
            ),
        },
    ]);

    return (
        <DrawerCollapsible title={tF("sections.general")}>
            <PropertiesList list={list} />
        </DrawerCollapsible>
    );
};
